/* istanbul ignore file */
/* there is test for general flow in cypress */
import { Button, Modal, Tabs, Icon } from '@distribusion/owl-frontend-ui'
import { useFormikContext } from 'formik'
import React, { ReactElement, useCallback, useMemo, useState, useEffect } from 'react'

import { SearchFormState } from '@components/SearchForm'
import StopoverDropdown from '@components/SearchForm/StopOver/ModalField/StopoverDropdown'
import config from '@config'
import { useTranslation } from '@lib/i18n'
import { useSettings } from '@queries/settings'
import { ViaRule } from 'src/stores/params'

import '@components/SearchForm/StopOver/ModalField/Modal.scss'

interface StopoverModalProps {
  opened: boolean
  onClose: () => void
}

type RuleType = 'include' | 'exclude'

interface LocalStationLine {
  id: string
  station: Location.NamedItem | null
  rule: RuleType
}

const StopoverModal = ({ opened, onClose }: StopoverModalProps): ReactElement => {
  const { t } = useTranslation()
  const { values, setFieldValue } = useFormikContext<SearchFormState>()
  const [{ stopOverFilter }] = useSettings()

  const { stopOverStations = [], viasRules = {} } = values

  const [localLines, setLocalLines] = useState<LocalStationLine[]>([])

  useEffect(() => {
    if (opened) {
      if (stopOverStations.length === 0) {
        setLocalLines([{ id: 'line-0', station: null, rule: stopOverFilter.default }])
      } else {
        const lines = stopOverStations.map((station, index) => ({
          id: `line-${index}`,
          station,
          rule: (viasRules[station.code] || stopOverFilter.default) as RuleType,
        }))
        setLocalLines(lines)
      }
    }
  }, [opened, stopOverStations, viasRules, stopOverFilter.default])

  const excludedStations = useMemo(
    () => localLines.filter(line => line.station && line.station.code).map(line => line.station!.code),
    [localLines],
  )

  const ruleOptions = useMemo(() => {
    const options = [{ value: 'include', label: t('searchBar.stopOverModal.include', 'Include') }]

    if (stopOverFilter.ruleSelection || stopOverFilter.default === 'exclude') {
      options.push({ value: 'exclude', label: t('searchBar.stopOverModal.exclude', 'Exclude') })
    }

    return options
  }, [t, stopOverFilter.ruleSelection, stopOverFilter.default])

  const handleAddNewLine = useCallback(() => {
    if (localLines.length >= config.maxStopOverStationsCount) {
      return
    }

    const newLine: LocalStationLine = {
      id: `line-${Date.now()}`,
      station: null,
      rule: stopOverFilter.default,
    }
    setLocalLines(prev => [...prev, newLine])
  }, [localLines.length, stopOverFilter.default])

  const handleUpdateStation = useCallback((lineId: string, newStation: Location.NamedItem | null) => {
    setLocalLines(prev => prev.map(line => (line.id === lineId ? { ...line, station: newStation } : line)))
  }, [])

  const handleClearLine = useCallback((lineId: string) => {
    setLocalLines(prev => prev.filter(line => line.id !== lineId))
  }, [])

  const handleLineRuleChange = useCallback((lineId: string, newRule: RuleType) => {
    setLocalLines(prev => prev.map(line => (line.id === lineId ? { ...line, rule: newRule } : line)))
  }, [])

  const handleCancel = (): void => {
    onClose()
  }

  const handleApply = (): void => {
    const validLines = localLines.filter(line => line.station !== null)
    const newStations = validLines.map(line => line.station!)
    const newRules: ViaRule = {}

    validLines.forEach(line => {
      if (line.station) {
        newRules[line.station.code] = line.rule
      }
    })

    setFieldValue('stopOverStations', newStations)
    setFieldValue('viasRules', newRules)
    onClose()
  }

  const modalFooter = (
    <div className="stopover-modal__footer">
      <div className="stopover-modal__footer-buttons">
        <Button variant="outline" onClick={handleCancel}>
          {t('searchBar.stopOverModal.cancel', 'Cancel')}
        </Button>
        <div className="stopover-modal__apply-button">
          <Button fullWidth variant="filled" onClick={handleApply}>
            {t('searchBar.stopOverModal.apply', 'Apply')}
          </Button>
        </div>
      </div>
    </div>
  )

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('searchBar.stopOverModal.title', 'Add stopovers')}
      className="stopover-modal"
      footer={modalFooter}
      maxWidth="lg"
    >
      <div className="stopover-modal__content">
        <div className="stopover-modal__section">
          <div className="stopover-modal__stations-list">
            {localLines.map((line, index) => {
              return (
                <div key={line.id} className="stopover-modal__station-item">
                  <div>{t('searchBar.stopOverModal.stopoverNumber', 'Stopover {{number}}', { number: index + 1 })}</div>
                  <div className="stopover-modal__station-controls">
                    <Tabs
                      options={ruleOptions}
                      value={line.rule}
                      onClick={(rule: string) => handleLineRuleChange(line.id, rule as RuleType)}
                      variant="outlined"
                    />
                    <div className="stopover-modal__dropdown-container">
                      <StopoverDropdown
                        value={line.station}
                        onChange={newStation => handleUpdateStation(line.id, newStation)}
                        excludedStations={excludedStations.filter(code => !line.station || code !== line.station.code)}
                        placeholder={t('searchBar.stopOverModal.selectStation', 'Select a station')}
                      />
                    </div>
                    <Button variant="text" onClick={() => handleClearLine(line.id)}>
                      <Icon name="cross" size="medium" className="mr-1" />
                      {t('searchBar.stopOverModal.clear', 'Delete')}
                    </Button>
                  </div>
                </div>
              )
            })}
          </div>

          <div className="stopover-modal__add-button">
            <Button
              variant="text"
              onClick={handleAddNewLine}
              fullWidth
              disabled={localLines.length >= config.maxStopOverStationsCount}
            >
              <Icon name="plus" size="medium" className="mr-1" />
              {t('searchBar.stopOverModal.addStation', 'Add stopover')}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default StopoverModal
